import { LocalStorage } from 'shared/helpers/localStorage';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { getLocalConversationWallpaper } from 'dashboard/helper/localWallpaperHelper';
import {
  PRIMARY_COLOR_OPTIONS,
  buildPrimaryColorVariables,
} from 'dashboard/helper/primaryColorHelper';

const LEGACY_PRIMARY_COLOR_VARIABLES = {
  iris: {
    '--blue-3': '240 241 254',
    '--blue-4': '230 231 255',
    '--blue-5': '218 220 255',
    '--blue-8': '155 158 240',
    '--blue-9': '91 91 214',
    '--blue-10': '81 81 205',
    '--blue-11': '87 83 198',
    '--solid-blue': '230 231 255',
    '--border-blue': '91, 91, 214, 0.5',
  },
  blue: {
    '--blue-3': '233 243 255',
    '--blue-4': '218 236 255',
    '--blue-5': '201 226 255',
    '--blue-8': '117 171 247',
    '--blue-9': '39 129 246',
    '--blue-10': '16 115 233',
    '--blue-11': '8 109 224',
    '--solid-blue': '218 236 255',
    '--border-blue': '39, 129, 246, 0.5',
  },
  green: {
    '--blue-3': '224 248 243',
    '--blue-4': '204 243 234',
    '--blue-5': '184 234 224',
    '--blue-8': '83 185 171',
    '--blue-9': '18 165 148',
    '--blue-10': '13 155 138',
    '--blue-11': '0 133 115',
    '--solid-blue': '204 243 234',
    '--border-blue': '18, 165, 148, 0.5',
  },
  amber: {
    '--blue-3': '255 247 194',
    '--blue-4': '255 238 156',
    '--blue-5': '251 229 119',
    '--blue-8': '226 163 54',
    '--blue-9': '255 163 26',
    '--blue-10': '232 132 0',
    '--blue-11': '171 100 0',
    '--solid-blue': '255 238 156',
    '--border-blue': '255, 163, 26, 0.5',
  },
  violet: {
    '--blue-3': '244 240 254',
    '--blue-4': '235 228 255',
    '--blue-5': '225 217 255',
    '--blue-8': '169 153 236',
    '--blue-9': '110 86 207',
    '--blue-10': '100 84 196',
    '--blue-11': '101 85 183',
    '--solid-blue': '235 228 255',
    '--border-blue': '110, 86, 207, 0.5',
  },
  ruby: {
    '--blue-3': '254 234 237',
    '--blue-4': '255 220 225',
    '--blue-5': '255 206 214',
    '--blue-8': '229 146 163',
    '--blue-9': '255 38 91',
    '--blue-10': '229 70 102',
    '--blue-11': '202 36 77',
    '--solid-blue': '255 220 225',
    '--border-blue': '255, 38, 91, 0.5',
  },
  cyan: {
    '--blue-3': '207 250 254',
    '--blue-4': '165 243 252',
    '--blue-5': '103 232 249',
    '--blue-8': '34 211 238',
    '--blue-9': '8 145 178',
    '--blue-10': '14 116 144',
    '--blue-11': '21 94 117',
    '--solid-blue': '165 243 252',
    '--border-blue': '8, 145, 178, 0.5',
  },
  lime: {
    '--blue-3': '236 252 203',
    '--blue-4': '217 249 157',
    '--blue-5': '190 242 100',
    '--blue-8': '132 204 22',
    '--blue-9': '101 163 13',
    '--blue-10': '77 124 15',
    '--blue-11': '63 98 18',
    '--solid-blue': '217 249 157',
    '--border-blue': '101, 163, 13, 0.5',
  },
  orange: {
    '--blue-3': '255 237 213',
    '--blue-4': '254 215 170',
    '--blue-5': '253 186 116',
    '--blue-8': '251 146 60',
    '--blue-9': '249 115 22',
    '--blue-10': '234 88 12',
    '--blue-11': '194 65 12',
    '--solid-blue': '254 215 170',
    '--border-blue': '249, 115, 22, 0.5',
  },
  red: {
    '--blue-3': '254 226 226',
    '--blue-4': '254 202 202',
    '--blue-5': '252 165 165',
    '--blue-8': '248 113 113',
    '--blue-9': '220 38 38',
    '--blue-10': '185 28 28',
    '--blue-11': '153 27 27',
    '--solid-blue': '254 202 202',
    '--border-blue': '220, 38, 38, 0.5',
  },
  slate: {
    '--blue-3': '241 245 249',
    '--blue-4': '226 232 240',
    '--blue-5': '203 213 225',
    '--blue-8': '100 116 139',
    '--blue-9': '71 85 105',
    '--blue-10': '51 65 85',
    '--blue-11': '30 41 59',
    '--solid-blue': '226 232 240',
    '--border-blue': '71, 85, 105, 0.5',
  },
};

// Cores novas usam a escala gerada; as antigas preservam os valores originais.
const PRIMARY_COLOR_VARIABLES = PRIMARY_COLOR_OPTIONS.reduce(
  (variables, option) => ({
    ...variables,
    [option.key]:
      LEGACY_PRIMARY_COLOR_VARIABLES[option.key] ||
      buildPrimaryColorVariables(option.hex),
  }),
  { ...LEGACY_PRIMARY_COLOR_VARIABLES }
);

const DARK_SCHEME_VARIABLES = {
  mint: {
    '--background-color': '13 26 29',
    '--surface-1': '14 29 33',
    '--surface-2': '17 38 43',
    '--solid-1': '14 29 33',
    '--solid-2': '17 38 43',
    '--solid-3': '23 51 58',
    '--alpha-1': '18, 59, 66, 0.55',
    '--alpha-3': '18, 45, 51, 0.92',
  },
  navy: {
    '--background-color': '23 34 54',
    '--surface-1': '23 34 54',
    '--surface-2': '31 45 70',
    '--solid-1': '23 34 54',
    '--solid-2': '31 45 70',
    '--solid-3': '42 58 88',
    '--alpha-1': '57, 75, 115, 0.52',
    '--alpha-3': '36, 50, 77, 0.94',
  },
  mirage: {
    '--background-color': '12 20 34',
    '--surface-1': '13 21 36',
    '--surface-2': '18 29 48',
    '--solid-1': '13 21 36',
    '--solid-2': '18 29 48',
    '--solid-3': '25 40 66',
    '--alpha-1': '31, 52, 85, 0.55',
    '--alpha-3': '18, 29, 48, 0.94',
  },
  cinder: {
    '--background-color': '18 18 20',
    '--surface-1': '20 20 23',
    '--surface-2': '28 28 32',
    '--solid-1': '20 20 23',
    '--solid-2': '28 28 32',
    '--solid-3': '43 43 49',
    '--alpha-1': '48, 48, 55, 0.55',
    '--alpha-3': '30, 30, 35, 0.94',
  },
  black: {
    '--background-color': '6 6 7',
    '--surface-1': '8 8 9',
    '--surface-2': '13 13 15',
    '--solid-1': '8 8 9',
    '--solid-2': '13 13 15',
    '--solid-3': '26 26 30',
    '--alpha-1': '34, 34, 39, 0.55',
    '--alpha-3': '14, 14, 16, 0.96',
  },
  forest: {
    '--background-color': '9 22 16',
    '--surface-1': '12 28 21',
    '--surface-2': '17 40 30',
    '--solid-1': '12 28 21',
    '--solid-2': '17 40 30',
    '--solid-3': '26 58 43',
    '--alpha-1': '27, 72, 52, 0.52',
    '--alpha-3': '18, 48, 35, 0.94',
  },
  plum: {
    '--background-color': '24 14 28',
    '--surface-1': '29 17 34',
    '--surface-2': '44 25 52',
    '--solid-1': '29 17 34',
    '--solid-2': '44 25 52',
    '--solid-3': '65 37 77',
    '--alpha-1': '74, 44, 86, 0.5',
    '--alpha-3': '45, 26, 53, 0.94',
  },
  mocha: {
    '--background-color': '27 20 16',
    '--surface-1': '33 24 19',
    '--surface-2': '48 35 27',
    '--solid-1': '33 24 19',
    '--solid-2': '48 35 27',
    '--solid-3': '68 50 39',
    '--alpha-1': '78, 56, 42, 0.5',
    '--alpha-3': '49, 35, 27, 0.94',
  },
  graphite: {
    '--background-color': '14 19 23',
    '--surface-1': '17 23 28',
    '--surface-2': '25 34 41',
    '--solid-1': '17 23 28',
    '--solid-2': '25 34 41',
    '--solid-3': '38 51 61',
    '--alpha-1': '47, 65, 77, 0.5',
    '--alpha-3': '26, 35, 42, 0.94',
  },
  burgundy: {
    '--background-color': '27 13 18',
    '--surface-1': '33 16 22',
    '--surface-2': '49 23 32',
    '--solid-1': '33 16 22',
    '--solid-2': '49 23 32',
    '--solid-3': '72 34 46',
    '--alpha-1': '83, 38, 53, 0.5',
    '--alpha-3': '51, 24, 33, 0.94',
  },
  olive: {
    '--background-color': '21 22 12',
    '--surface-1': '26 27 15',
    '--surface-2': '39 41 21',
    '--solid-1': '26 27 15',
    '--solid-2': '39 41 21',
    '--solid-3': '58 61 31',
    '--alpha-1': '70, 74, 37, 0.5',
    '--alpha-3': '41, 43, 23, 0.94',
  },
};

const LIGHT_SCHEME_VARIABLES = {
  snow: {
    '--background-color': '248 250 252',
    '--surface-1': '252 253 255',
    '--surface-2': '255 255 255',
    '--solid-1': '255 255 255',
    '--solid-2': '255 255 255',
    '--solid-3': '248 250 252',
    '--border-container': '226 232 240',
    '--border-weak': '232 237 243',
    '--border-strong': '203 213 225',
    '--alpha-1': '148, 163, 184, 0.12',
    '--alpha-2': '100, 116, 139, 0.1',
    '--alpha-3': '255, 255, 255, 0.96',
    '--card-color': '255 255 255',
    '--label-background': '248 250 252',
  },
  ivory: {
    '--background-color': '250 247 242',
    '--surface-1': '255 252 247',
    '--surface-2': '255 255 255',
    '--solid-1': '255 255 255',
    '--solid-2': '255 252 247',
    '--solid-3': '250 245 237',
    '--border-container': '235 226 214',
    '--border-weak': '241 234 224',
    '--border-strong': '218 205 188',
    '--alpha-1': '180, 154, 122, 0.12',
    '--alpha-2': '152, 123, 87, 0.1',
    '--alpha-3': '255, 252, 247, 0.96',
    '--card-color': '255 252 247',
    '--label-background': '250 247 242',
  },
  mint: {
    '--background-color': '242 250 247',
    '--surface-1': '248 253 251',
    '--surface-2': '255 255 255',
    '--solid-1': '255 255 255',
    '--solid-2': '248 253 251',
    '--solid-3': '238 248 244',
    '--border-container': '211 232 224',
    '--border-weak': '224 240 234',
    '--border-strong': '184 218 205',
    '--alpha-1': '84, 160, 132, 0.12',
    '--alpha-2': '55, 130, 104, 0.09',
    '--alpha-3': '248, 253, 251, 0.96',
    '--card-color': '255 255 255',
    '--label-background': '242 250 247',
  },
  sky: {
    '--background-color': '243 248 253',
    '--surface-1': '249 252 255',
    '--surface-2': '255 255 255',
    '--solid-1': '255 255 255',
    '--solid-2': '249 252 255',
    '--solid-3': '238 246 253',
    '--border-container': '211 226 240',
    '--border-weak': '225 236 247',
    '--border-strong': '185 211 234',
    '--alpha-1': '84, 145, 196, 0.11',
    '--alpha-2': '50, 118, 176, 0.09',
    '--alpha-3': '249, 252, 255, 0.96',
    '--card-color': '255 255 255',
    '--label-background': '243 248 253',
  },
  lavender: {
    '--background-color': '248 246 253',
    '--surface-1': '252 250 255',
    '--surface-2': '255 255 255',
    '--solid-1': '255 255 255',
    '--solid-2': '252 250 255',
    '--solid-3': '245 241 252',
    '--border-container': '226 217 240',
    '--border-weak': '237 230 247',
    '--border-strong': '208 194 229',
    '--alpha-1': '126, 98, 173, 0.11',
    '--alpha-2': '102, 73, 153, 0.09',
    '--alpha-3': '252, 250, 255, 0.96',
    '--card-color': '255 255 255',
    '--label-background': '248 246 253',
  },
  rose: {
    '--background-color': '253 246 248',
    '--surface-1': '255 251 252',
    '--surface-2': '255 255 255',
    '--solid-1': '255 255 255',
    '--solid-2': '255 251 252',
    '--solid-3': '252 240 244',
    '--border-container': '241 218 226',
    '--border-weak': '248 231 237',
    '--border-strong': '229 195 207',
    '--alpha-1': '184, 91, 123, 0.1',
    '--alpha-2': '158, 62, 95, 0.08',
    '--alpha-3': '255, 251, 252, 0.96',
    '--card-color': '255 255 255',
    '--label-background': '253 246 248',
  },
  sand: {
    '--background-color': '249 246 238',
    '--surface-1': '254 251 244',
    '--surface-2': '255 255 255',
    '--solid-1': '255 255 255',
    '--solid-2': '254 251 244',
    '--solid-3': '246 240 227',
    '--border-container': '232 222 198',
    '--border-weak': '241 233 214',
    '--border-strong': '215 200 168',
    '--alpha-1': '161, 133, 76, 0.11',
    '--alpha-2': '139, 109, 50, 0.09',
    '--alpha-3': '254, 251, 244, 0.96',
    '--card-color': '255 255 255',
    '--label-background': '249 246 238',
  },
};

const DARK_DOODLE_PATTERN = `url("/dashboard/images/bg-chat-tile-dark.png")`;
const CLASSIC_CROSS_PATTERN = `url("/dashboard/images/bg-chat-tile-light.png")`;

export const WALLPAPER_OPTIONS = {
  none: { bg: null, pattern: null },
  whatsapp: { bg: '#dce9d3', pattern: null },
  beige: { bg: '#ece5dd', pattern: null },
  sky: { bg: '#d5e8f5', pattern: null },
  lavender: { bg: '#e4d8f5', pattern: null },
  rose: { bg: '#f5d8e8', pattern: null },
  mist: { bg: '#e8eef4', pattern: null },
  mint: { bg: '#dff2ea', pattern: null },
  peach: { bg: '#f8e2d1', pattern: null },
  graphite: { bg: '#172033', pattern: null },
  aurora: {
    bg: '#dff7f2',
    pattern: 'linear-gradient(135deg, #dff7f2 0%, #dce8ff 52%, #eadffc 100%)',
    repeat: 'no-repeat',
    size: 'cover',
  },
  sunset: {
    bg: '#f9e3d0',
    pattern: 'linear-gradient(135deg, #f9e3d0 0%, #f4d6df 55%, #ddd9f7 100%)',
    repeat: 'no-repeat',
    size: 'cover',
  },
  dark_pattern: { bg: '#111b21', pattern: DARK_DOODLE_PATTERN },
  classic: { bg: '#efeae2', pattern: CLASSIC_CROSS_PATTERN },
  custom: { bg: null, pattern: null, custom: true },
};

// Backward-compat alias
export const WALLPAPER_COLORS = Object.fromEntries(
  Object.entries(WALLPAPER_OPTIONS)
    .filter(([, v]) => v.bg)
    .map(([k, v]) => [k, v.bg])
);

const clearVariables = variableGroups => {
  variableGroups.forEach(group => {
    Object.keys(group).forEach(variable => {
      document.body.style.removeProperty(variable);
    });
  });
};

const applyVariables = variables => {
  Object.entries(variables || {}).forEach(([variable, value]) => {
    document.body.style.setProperty(variable, value);
  });
};

export const setPrimaryColor = color => {
  clearVariables(Object.values(PRIMARY_COLOR_VARIABLES));
  applyVariables(
    PRIMARY_COLOR_VARIABLES[color] || PRIMARY_COLOR_VARIABLES.blue
  );
};

export const setDarkColorScheme = isDarkMode => {
  clearVariables(Object.values(DARK_SCHEME_VARIABLES));
  if (!isDarkMode) return;

  const selectedDarkScheme =
    LocalStorage.get(LOCAL_STORAGE_KEYS.DARK_COLOR_SCHEME) || 'default';
  if (selectedDarkScheme === 'default') return;

  applyVariables(DARK_SCHEME_VARIABLES[selectedDarkScheme]);
};

export const setLightColorScheme = isDarkMode => {
  clearVariables(Object.values(LIGHT_SCHEME_VARIABLES));
  if (isDarkMode) return;

  const selectedLightScheme =
    LocalStorage.get(LOCAL_STORAGE_KEYS.LIGHT_COLOR_SCHEME) || 'default';
  if (selectedLightScheme === 'default') return;

  applyVariables(LIGHT_SCHEME_VARIABLES[selectedLightScheme]);
};

let customWallpaperObjectUrl;
let wallpaperRequestId = 0;

const revokeCustomWallpaperUrl = () => {
  if (customWallpaperObjectUrl) {
    URL.revokeObjectURL(customWallpaperObjectUrl);
    customWallpaperObjectUrl = undefined;
  }
};

const applyCustomConversationWallpaper = async requestId => {
  let asset;
  try {
    asset = await getLocalConversationWallpaper();
  } catch {
    asset = null;
  }

  const selectedWallpaper = LocalStorage.get(
    LOCAL_STORAGE_KEYS.CONVERSATION_WALLPAPER
  );
  if (requestId !== wallpaperRequestId || selectedWallpaper !== 'custom') {
    return;
  }

  revokeCustomWallpaperUrl();
  if (!asset?.blob) {
    document.body.style.removeProperty('--conversation-bg');
    document.body.style.removeProperty('--conversation-pattern');
    return;
  }

  customWallpaperObjectUrl = URL.createObjectURL(asset.blob);
  document.body.style.setProperty('--conversation-bg', '#111827');
  document.body.style.setProperty(
    '--conversation-pattern',
    `url("${customWallpaperObjectUrl}")`
  );
};

export const setConversationWallpaper = () => {
  const key =
    LocalStorage.get(LOCAL_STORAGE_KEYS.CONVERSATION_WALLPAPER) || 'none';
  const option = WALLPAPER_OPTIONS[key];
  wallpaperRequestId += 1;

  if (option?.custom) {
    applyCustomConversationWallpaper(wallpaperRequestId).catch(() => {});
    return;
  }

  revokeCustomWallpaperUrl();
  if (option?.bg) {
    document.body.style.setProperty('--conversation-bg', option.bg);
    document.body.style.setProperty(
      '--conversation-pattern',
      option.pattern || 'none'
    );
  } else {
    document.body.style.removeProperty('--conversation-bg');
    document.body.style.removeProperty('--conversation-pattern');
  }
};

export const setColorTheme = isOSOnDarkMode => {
  const selectedColorScheme =
    LocalStorage.get(LOCAL_STORAGE_KEYS.COLOR_SCHEME) || 'auto';
  const isDarkMode =
    (selectedColorScheme === 'auto' && isOSOnDarkMode) ||
    selectedColorScheme === 'dark';
  if (isDarkMode) {
    document.body.classList.add('dark');
    document.documentElement.style.setProperty('color-scheme', 'dark');
  } else {
    document.body.classList.remove('dark');
    document.documentElement.style.setProperty('color-scheme', 'light');
  }
  setDarkColorScheme(isDarkMode);
  setLightColorScheme(isDarkMode);
  setPrimaryColor(LocalStorage.get(LOCAL_STORAGE_KEYS.PRIMARY_COLOR) || 'blue');
  setConversationWallpaper();
};
